import React from "react";
import { useNavigate } from "react-router-dom";

import CachedIcon from "@mui/icons-material/Cached";
import { Button } from "@mui/material";
import { keyframes, styled } from "@mui/system";
import { GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector } from "@mui/x-data-grid";

import { useAutoUpdate } from "../../contexts/AutoUpdateContext";
import { UserRole } from "../../services/api/AuthUser";
import RoleBasedRender from "../shared/RoleBasedRender";
import JobsMultiFilterButton from "./JobMultiFilterButton";
import JobsExportButton from "./JobsExportButton";
import JobRetranscodeButton from "./JobsRetranscodeButton";

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(-360deg);
  }
`;

const RotateIcon = styled(CachedIcon, {
  shouldForwardProp: (prop) => prop !== "isRotating",
})<{ isRotating: boolean }>(({ isRotating }) => ({
  animation: `${rotate} 2s linear infinite`,
  animationPlayState: isRotating ? "running" : "paused",
  transition: "opacity 0.3s ease-in-out",
}));

const AutoReloadButton: React.FC = () => {
  const autoUpdate = useAutoUpdate();
  const handleClick = () => autoUpdate.setActive(!autoUpdate.active);

  return (
    <Button size="small" startIcon={<RotateIcon isRotating={autoUpdate.active} />} onClick={handleClick}>
      Auto Update
    </Button>
  );
};

const JobsTableBar: React.FC<{ ids?: string[]; backfill?: boolean }> = (props) => {
  const navigate = useNavigate();

  if (props.backfill) {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
        <AutoReloadButton />
        <div style={{ flexGrow: 1 }}></div>
        <Button size="small" variant="contained" onClick={() => navigate("/backfill/create")}>
          Create Backfill
        </Button>
      </GridToolbarContainer>
    );
  } else {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <JobsMultiFilterButton />
        <GridToolbarDensitySelector />
        <JobsExportButton />
        <AutoReloadButton />
        <div style={{ flexGrow: 1 }}></div>
        <RoleBasedRender requiredRoles={[UserRole.ADMIN, UserRole.OPERATOR]}>
          <JobRetranscodeButton ids={props.ids!} />
          <Button size="small" variant="contained" onClick={() => navigate("/jobs/create")}>
            Create Job
          </Button>
        </RoleBasedRender>
      </GridToolbarContainer>
    );
  }
};

export default JobsTableBar;
